import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiGet, useApiPost } from '../../api/Api.hooks';
import { Message, MessageType } from '../../models/Message';
import { usePubSubNewMessages } from '../session/usePubSubNewMessages';
import { PaginatedList } from '@people-science/core';
import { useNotificationContext } from '../../context/NotificationContext';
import { usePubSubNewRoomMessage } from '../session/usePubSubNewRoomMessage';
import { usePubSubNewUserMessage } from '../session/usePubSubNewUserMessage';

export function useRoom(userId: number, roomId: number) {
  const {
    data: loadedMessages,
    isLoading,
    error: loadingError,
    get: getMessages
  } = useApiGet<
    PaginatedList<Message> & { archived: boolean; canPost?: boolean }
  >(`rooms/${roomId}/messages`);

  const { data: trackedViewedMessages, post: trackViewedMessages } = useApiPost(
    `rooms/${roomId}/viewed`
  );

  const {
    data: createdMessages,
    isLoading: isPosting,
    error: postingError,
    clearData: clearNewMessage,
    post
  } = useApiPost<Message[]>(`rooms/${roomId}/messages`);

  const { userDevice } = useNotificationContext();

  // subscribe to new messages in the room
  const { messages: pubSubRoomMessages, notifyNewRoomMessage } =
    usePubSubNewRoomMessage(roomId, 'ChatRoom', true);
  const pubSubMessageRef = useRef(pubSubRoomMessages); // last pubSubMessage reference

  // send notifications after posting a message
  const { notifyNewMessage } = usePubSubNewMessages('ChatRoom');
  const { notifyNewUserMessage } = usePubSubNewUserMessage(userId);

  // Append created messages and reverse the sort order from bottom up
  const [messages, setMessages] = useState<Message[]>([]);
  const [queue, setQueue] = useState<Message[]>([]);
  useEffect(() => {
    if (!loadedMessages) return;
    const messageText =
      createdMessages?.[0]?.body?.text || pubSubRoomMessages?.[0]?.body?.text;
    if (createdMessages) {
      // if created new message then append to array
      clearNewMessage(); // clear api cache to prevent duplicate messages
      notifyNewRoomMessage(createdMessages); // notify real-time to all room users
      notifyNewUserMessage(); // notify user that a new message occured
      notifyNewMessage(); // notify Chloe Cloud inbox a new message occured
      const [nextMessage, ...queuedMessages] = createdMessages;
      loadedMessages.data.push(nextMessage); // apend new message
      setQueue(queuedMessages); // queue remaining messages to animate in
    } else if (
      pubSubRoomMessages &&
      pubSubRoomMessages !== pubSubMessageRef.current
    ) {
      // if received a real-time sync of new message then append to array
      pubSubMessageRef.current = pubSubRoomMessages; // track previous PubSub messages
      const [nextMessage, ...queuedMessages] = pubSubRoomMessages;
      const index = loadedMessages.data.findIndex(m => m.id === nextMessage.id);
      if (index > -1) {
        loadedMessages.data[index] = nextMessage;
      } else {
        loadedMessages.data.push(nextMessage);
      }
      setQueue(queuedMessages); // queue remaining messages to animate in
    }
    if (messageText?.toLowerCase() === 'reset') {
      // if reseting, empty out messages
      loadedMessages.data.length = 0;
      setMessages([]);
    }
    // sort messages newest at the bottom
    setMessages(loadedMessages.data);
  }, [
    createdMessages,
    pubSubRoomMessages,
    loadedMessages,
    notifyNewRoomMessage,
    notifyNewMessage,
    clearNewMessage
  ]);

  // animate queued messages into main message array
  const timeoutRef = useRef<any>();
  useEffect(() => {
    if (loadedMessages && queue.length > 0) {
      timeoutRef.current = setTimeout(() => {
        const [message, ...messages] = queue;
        const index = loadedMessages.data.findIndex(m => m.id === message.id);
        if (index > -1) {
          loadedMessages.data[index] = message;
        } else {
          loadedMessages.data.push(message);
        }
        setQueue(messages);
      }, 700);
    }
  }, [loadedMessages, queue]);
  // const messages = useMemo(() => {
  // }, [
  //   loadedMessages,
  //   createdMessage,
  //   pubSubMessages,
  //   clearNewMessage,
  //   notifyNewMessage
  // ]);

  const postMessage = useCallback(
    (type: MessageType, body: any) => {
      post({ roomId, type, body, userDeviceId: userDevice?.id });
    },
    [post, roomId, userDevice?.id]
  );

  return {
    // sort by id because system generated responses could have exact same
    // timestamp in created field due to millisecond response times.
    messages: useMemo(
      () => loadedMessages?.data.sort((a, b) => a.id - b.id) || [],
      [loadedMessages?.data]
    ),
    total: loadedMessages?.total || 0,
    page: loadedMessages?.page || 0,
    error: loadingError || postingError,
    isPosting,
    isLoading,
    getMessages,
    postMessage,
    trackedViewedMessages,
    trackViewedMessages,
    archived: loadedMessages?.archived,
    // canPost is the new API field; coordinators get true even when the
    // room is archived so they can reach back out to a withdrawn or
    // completed participant. Older API versions (pre-CD-87) don't return
    // canPost — fall back to !archived for backwards compat.
    canPost: loadedMessages
      ? (loadedMessages.canPost ?? !loadedMessages.archived)
      : false
  };
}
